// Pure helpers for the Event Summary (RP-2171) dashboard: the legacy "Avg Ticket" stat and the
// CSV serialization of the headline financial summary. Kept out of the page component so they can
// be unit-tested without pulling in React / ag-grid / recharts.
import { MetricKey, ReportResponse } from "../api/types";
import { centsToUsd, formatMetric, METRIC_LABELS } from "./format";

// Avg Ticket (legacy Event Summary stat) = Gross ÷ Transactions, in cents, matching the OLD report's
// formula (event-summary-report/helpers.buildEventStats: totalGross / totalTransactions). Guarded
// for zero transactions (returns 0 → "$0.00", like legacy). NOTE the magnitude can differ from the
// old report: the new canonical Gross (Subtotal+Tax) and the approved-Transaction count are the
// corrected engine definitions, not the legacy Hasura view — the DEFINITION (gross per transaction)
// is preserved; the money isn't redefined here.
export const avgTicketCents = (txns: number, gross: number): number =>
  txns > 0 ? Math.round(gross / txns) : 0;

// Minimal RFC-4180 CSV cell: always quote and escape embedded quotes. Money strings carry commas
// ("$1,234.56") so quoting is required to keep each value in one column.
const csvCell = (v: string | number): string => `"${String(v).replace(/"/g, '""')}"`;

// Build the headline financial summary (the KPI cards + Avg Ticket) as CSV text — one Metric,Value
// row per figure on screen. Top Products has its own export (the MetricGrid); this covers the
// summary itself so the whole Event Summary report is exportable. Lines are CRLF-joined (Excel).
export function buildEventSummaryCsv(
  summary: ReportResponse,
  metrics: MetricKey[],
  avgCents: number
): string {
  const lines: string[] = [];
  lines.push(csvCell(`Event Summary — ${summary.window.from} to ${summary.window.to}`));
  lines.push([csvCell("Metric"), csvCell("Value")].join(","));
  for (const m of metrics) {
    lines.push([csvCell(METRIC_LABELS[m]), csvCell(formatMetric(m, summary.totals[m]))].join(","));
  }
  lines.push([csvCell("Avg Ticket"), csvCell(centsToUsd(avgCents))].join(","));
  return lines.join("\r\n");
}

// Suggested download filename for the summary CSV, mirroring MetricGrid's `<name>_new_<from>_to_<to>`.
export const eventSummaryCsvFilename = (summary: ReportResponse): string =>
  `EventSummary_new_${summary.window.from}_to_${summary.window.to}.csv`;
